"use client"
import { m } from "framer-motion"
import { useState, useEffect, useRef } from "react"

export const GrowSmarter = () => {
    const [isVisible, setIsVisible] = useState(false)
    const sectionRef = useRef(null)

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isVisible) {
                    setIsVisible(true)
                }
            },
            { threshold: 0.1 }
        )

        if (sectionRef.current) {
            observer.observe(sectionRef.current)
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current)
            }
        }
    }, [isVisible])

    return (
        <>
            <section ref={sectionRef} className="relative pt-[60px] md:pt-[120px] pb-[60px] px-4">
                {/* h2, not h4: this renders between two <h2> sections on /growsuite, so h4 skipped
                    a level and broke the heading outline for screen-reader navigation. Styling is
                    unchanged — the size comes from the classes, not the tag. */}
                <h2 className="text-[32px] text-center  md:text-[40px] font-normal leading-[120%] text-[#090D28] font-NeueMontreal">
                    Everything You Need to{" "}
                    <span className="text-[32px] md:text-[40px] font-normal leading-[120%] text-[#3F92FF] font-NeueMontreal">
                        Grow Smarter
                    </span>
                </h2>

                <div className="w-full container mx-auto ">

                    <FlowingCurves isVisible={isVisible} />
                    <div className="flex z-50 relative mx-auto w-[100px] h-[100px] p-[10px] justify-center items-center gap-[10px] flex-shrink-0 aspect-square rounded-[24px] border-[4px] border-white bg-[radial-gradient(65.83%_100%_at_50%_0%,_#2EABFF_45.08%,_#00379E_100%)] shadow-[0px_44px_12px_rgba(8,56,164,0),0px_28px_11px_rgba(8,56,164,0.01),0px_16px_10px_rgba(8,56,164,0.05),0px_7px_7px_rgba(8,56,164,0.09),0px_2px_4px_rgba(8,56,164,0.1)] text-white text-center text-[14px] font-normal leading-[120%] font-Schibsted_Grotesk">
                        Vilva Growsuite
                    </div>
                </div>
            </section>
        </>
    )
}

const FlowingCurves = ({ isVisible }) => {
    const [screenSize, setScreenSize] = useState("lg")

    // Detect screen size dynamically
    useEffect(() => {
        const updateScreenSize = () => {
            const width = window.innerWidth
            if (width < 640) setScreenSize("mobile")
            else if (width < 640) setScreenSize("sm")
            else if (width < 1024) setScreenSize("md")
            else setScreenSize("lg")
        }
        updateScreenSize()
        window.addEventListener("resize", updateScreenSize)
        return () => window.removeEventListener("resize", updateScreenSize)
    }, [])

    // Define curves with dynamic properties based on screen size
    const curves = [
        {
            id: "curve1",
            path: screenSize === "md" || screenSize === "mobile" ? "M190.039 284.745C190.039 203.665 120.294 190.845 72.8117 169.31C16.2525 143.657 -7.07601 81.4286 -0.214952 0.925537" : screenSize === "lg" ? "M395.031 341.5C395.031 253 250.754 239.006 152.531 215.5C35.5312 187.5 -10.5 86 4 1" : "M238.019 205.5C238.019 151.8 150.452 143.404 91.5186 129.3C21.3187 112.5 -6.3 51.6 2.4 0.6",
            width: screenSize === "lg" ? 397 : screenSize === "md" || screenSize === "mobile" ? 191 : 238,
            height: screenSize === "lg" ? 342 : screenSize === "md" || screenSize === "mobile" ? 285 : 205,
            position: screenSize === "lg" ? "left-[660px]" : screenSize === "md" ? "left-[400px]" : screenSize === "sm" ? "left-[400px]" : "left-[400px]",
            text: "Attendance Tracking",
            endPoint: screenSize === "md" || screenSize === "mobile" ? { x: -0.214952, y: 0.925537 } : screenSize === "lg" ? { x: 4, y: 1 } : { x: 2.4, y: 0.6 },
        },
        {
            id: "curve2",
            path: screenSize === "md" || screenSize === "mobile" ? "M147.039 206.659C147.039 113.932 77.3167 104.038 43.8649 77.6204C17.7722 57.0144 1.5847 46.9916 1.5847 0.827637" : screenSize === "lg" ? "M303 243C303 133.529 158.478 121.848 89.139 90.6596C35.0537 66.3327 1.5 54.5 1.5 0" : "M181.8 145.8C181.8 80.1174 95.0868 73.1088 53.4834 54.3958C21.0322 39.7996 0.9 32.7 0.9 0",
            width: screenSize === "lg" ? 304 : screenSize === "md" || screenSize === "mobile" ? 148 : 182,
            height: screenSize === "lg" ? 243 : screenSize === "md" || screenSize === "mobile" ? 207 : 146,
            position: screenSize === "lg" ? "left-[355px]" : screenSize === "md" ? "left-[253px]" : screenSize === "sm" ? "left-[253px]" : "left-[253px]",
            text: "Lead Scoring",
            endPoint: screenSize === "md" || screenSize === "mobile" ? { x: 1.5847, y: 0.827637 } : screenSize === "lg" ? { x: 1.5, y: 0 } : { x: 0.9, y: 0 },
        },
        {
            id: "curve3",
            path: screenSize === "md" || screenSize === "mobile" ? "M107.039 245.574C106.711 132.684 47.6118 124.795 25.4161 90.0569C1.45111 52.5498 0.63899 23.4488 0.966973 0.405762" : screenSize === "lg" ? "M220.594 268.5C219.916 145.097 97.8458 136.473 52 98.5C2.5 57.5 0.822544 25.6889 1.5 0.5" : "M132.356 161.1C131.95 87.0582 58.7075 81.8838 31.2 59.1C1.5 34.5 0.493526 15.4133 0.9 0.3",
            width: screenSize === "lg" ? 222 : screenSize === "md" || screenSize === "mobile" ? 108 : 133,
            height: screenSize === "lg" ? 269 : screenSize === "md" || screenSize === "mobile" ? 246 : 161,
            position: screenSize === "lg" ? "left-[132px]" : screenSize === "md" ? "left-[144px]" : screenSize === "sm" ? "left-[144px]" : "left-[144px]",
            text: "Access Control",
            endPoint: screenSize === "md" || screenSize === "mobile" ? { x: 0.966973, y: 0.405762 } : screenSize === "lg" ? { x: 1.5, y: 0.5 } : { x: 0.9, y: 0.3 },
        },
        {
            id: "curve4",
            path: screenSize === "md" || screenSize === "mobile" ? "M83.0284 332.489C83.2703 312.821 82.0609 238.721 54.4881 194.81C26.9154 150.9 -3.84077 102.186 2.56243 0.642578" : screenSize === "lg" ? "M164 309C164.5 287.5 162 206.5 105 158.5C47.9996 110.5 -1.00021 77 1.49986 1" : "M44.4008 148.5C41.4006 114.3 56.4006 92.4 23.1006 54.9C-3.53939 24.9 0.600912 7.5 2.10104 0.6",
            width: screenSize === "lg" ? 77 : screenSize === "md" || screenSize === "mobile" ? 84 : 46,
            height: screenSize === "lg" ? 307 : screenSize === "md" || screenSize === "mobile" ? 333 : 149,
            position: screenSize === "lg" ? "right-[32px]" : screenSize === "md" ? "left-[61px]" : screenSize === "sm" ? "left-[61px]" : "left-[61px]",
            text: "Payroll Automation",
            endPoint: screenSize === "md" || screenSize === "mobile" ? { x: 2.56243, y: 0.642578 } : screenSize === "lg" ? { x: 3.50173, y: 1 } : { x: 2.10104, y: 0.6 },
        },
        {
            id: "curve5",
            path: screenSize === "md" || screenSize === "mobile" ? "M36.3572 226.659C33.9413 174.41 46.0197 140.951 19.2057 83.6599C-2.24546 37.8268 1.08841 11.2435 2.29635 0.701904" : screenSize === "lg" ? "M74.0014 247.5C69.001 190.5 94.001 154 38.501 91.5C-5.89898 41.5 1.00152 12.5 3.50173 1" : "M44.4008 148.5C41.4006 114.3 56.4006 92.4 23.1006 54.9C-3.53939 24.9 0.600912 7.5 2.10104 0.6",
            width: screenSize === "lg" ? 77 : screenSize === "md" || screenSize === "mobile" ? 38 : 46,
            height: screenSize === "lg" ? 248 : screenSize === "md" || screenSize === "mobile" ? 227 : 149,
            position: screenSize === "lg" ? "right-[20px]" : screenSize === "md" ? "left-[23px]" : screenSize === "sm" ? "left-[23px]" : "left-[23px]",
            text: "Campaign Manager",
            endPoint: screenSize === "md" || screenSize === "mobile" ? { x: 2.29635, y: 0.701904 } : screenSize === "lg" ? { x: 3.50173, y: 1 } : { x: 2.10104, y: 0.6 },
        },
        {
            id: "curve6",
            path: screenSize === "md" || screenSize === "mobile" ? "M1.03906 279.489C1.03906 236.493 10.1515 143.64 35.3305 88.2941C58.1115 35.6927 53.209 0.243896 53.209 0.243896" : screenSize === "lg" ? "M1 316C1 269 20 167.5 72.5 107C120 49.5 108.5 1 108.5 1" : "M0.6 189.6C0.6 161.4 12 100.5 43.5 64.2C72 29.7 65.1 0.6 65.1 0.6",
            width: screenSize === "lg" ? 111 : screenSize === "md" || screenSize === "mobile" ? 55 : 67,
            height: screenSize === "lg" ? 316 : screenSize === "md" || screenSize === "mobile" ? 280 : 190,
            position: screenSize === "lg" ? "right-[25px]" : screenSize === "md" ? "left-[22px]" : screenSize === "sm" ? "left-[22px]" : "left-[22px]",
            text: "Live Analytics",
            endPoint: screenSize === "md" || screenSize === "mobile" ? { x: 53.209, y: 0.243896 } : screenSize === "lg" ? { x: 108.5, y: 1 } : { x: 65.1, y: 0.6 },
        },
        {
            id: "curve7",
            path: screenSize === "md" || screenSize === "mobile" ? "M1.03906 334.489C1.36742 210.718 53.9383 163.634 73.131 119.052C90.0654 79.7151 77.9694 23.9118 85.2013 0.584229" : screenSize === "lg" ? "M1 366C1.67865 230.703 110.332 179.234 150 130.5C185 87.5 160 26.5 174.947 1" : "M0.6 219.6C1.00719 138.422 66.1992 107.54 90 78.3C111 52.5 96 15.9 104.968 0.6",
            width: screenSize === "lg" ? 176 : screenSize === "md" || screenSize === "mobile" ? 87 : 106,
            height: screenSize === "lg" ? 366 : screenSize === "md" || screenSize === "mobile" ? 335 : 220,
            position: screenSize === "lg" ? "right-[134px]" : screenSize === "md" ? "right-[32px]" : screenSize === "sm" ? "right-[32px]" : "right-[32px]",
            text: "Whatsapp Integration",
            endPoint: screenSize === "md" || screenSize === "mobile" ? { x: 85.2013, y: 0.584229 } : screenSize === "lg" ? { x: 174.947, y: 1 } : { x: 104.968, y: 0.6 },
        },
        {
            id: "curve8",
            path: screenSize === "md" || screenSize === "mobile" ? "M1.03906 265.574C1.36726 152.879 72.3285 124.455 94.5387 89.7768C118.519 52.3343 119.332 23.2834 119.004 0.280029" : screenSize === "lg" ? "M1 291C1.67746 167.597 148.154 136.473 194 98.5C243.5 57.5 245.177 25.6889 244.5 0.5" : "M0.6 174.6C1.00648 100.558 88.8924 81.8838 116.4 59.1C146.1 34.5 147.106 15.4133 146.7 0.3",
            width: screenSize === "lg" ? 246 : screenSize === "md" || screenSize === "mobile" ? 120 : 148,
            height: screenSize === "lg" ? 291 : screenSize === "md" || screenSize === "mobile" ? 266 : 175,
            position: screenSize === "lg" ? "right-[310px]" : screenSize === "md" ? "right-[119px]" : screenSize === "sm" ? "right-[119px]" : "right-[119px]",
            text: "Sales Pipeline",
            endPoint: screenSize === "md" || screenSize === "mobile" ? { x: 119.004, y: 0.280029 } : screenSize === "lg" ? { x: 244.5, y: 0.5 } : { x: 146.7, y: 0.3 },
        },
        {
            id: "curve9",
            path: screenSize === "md" || screenSize === "mobile" ? "M1.03906 221.404C1.03906 118.488 77.9178 109.386 111.335 80.0658C137.4 57.1957 148.806 39.5856 148.806 0.706299" : screenSize === "lg" ? "M1.5 260.5C1.5 148 160.77 138.051 230 106C284 81 318 43 318 0.5" : "M1.5 260.5C1.5 148 160.77 138.051 230 106C284 81 318 43 318 0.5",
            width: screenSize === "lg" ? 246 : screenSize === "md" || screenSize === "mobile" ? 150 : 148,
            height: screenSize === "lg" ? 261 : screenSize === "md" || screenSize === "mobile" ? 222 : 175,
            position: screenSize === "lg" ? "right-[555px]" : screenSize === "md" ? "right-[243px]" : screenSize === "sm" ? "right-[243px]" : "right-[243px]",
            text: "Self-Service",
            endPoint: screenSize === "md" || screenSize === "mobile" ? { x: 148.806, y: 0.706299 } : screenSize === "lg" ? { x: 310, y: 0.5 } : { x: 146.7, y: 0.3 },
        },
        {
            id: "curve10",
            path: screenSize === "md" || screenSize === "mobile" ? "M1.03906 302.404C1.03906 221.32 70.8691 208.499 118.409 186.963C175.037 161.31 198.027 78.394 191.009 0.517334" : screenSize === "lg" ? "M1.5 330.5C1.5 242 145.777 228.006 244 204.5C361 176.5 408.5 86 394 1" : "M0.6 174.6C1.00648 100.558 88.8924 81.8838 116.4 59.1C146.1 34.5 147.106 15.4133 146.7 0.3",
            width: screenSize === "lg" ? 246 : screenSize === "md" || screenSize === "mobile" ? 186 : 148,
            height: screenSize === "lg" ? 320 : screenSize === "md" || screenSize === "mobile" ? 303 : 175,
            position: screenSize === "lg" ? "right-[807px]" : screenSize === "md" ? "right-[390px]" : screenSize === "sm" ? "right-[390px]" : "right-[390px]",
            text: "Smart Automation",
            endPoint: screenSize === "md" || screenSize === "mobile" ? { x: 191.009, y: 0.517334 } : screenSize === "lg" ? { x: 390, y: 0.5 } : { x: 146.7, y: 0.3 },
        },
    ]

    return (
        <div className="h-[450px] relative ">

            {/* Flowing Curves Container */}
            <div className="absolute bottom-0 z-50 left-0 right-0 h-96 flex items-end justify-center">

                {curves.map((curve, index) => (
                    <m.svg
                        key={curve.id}
                        className={`relative z-40  bottom-0 overflow-visible font-Schibsted_Grotesk ${curve.position}`}
                        xmlns="http://www.w3.org/2000/svg"
                        width={curve.width}
                        height={curve.height}
                        viewBox={`0 0 ${curve.width} ${curve.height}`}
                        fill="none"
                        style={{ overflow: 'visible' }}
                        initial={{ opacity: 0, y: 50 }}
                        animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                        transition={{
                            duration: 1.5,
                            delay: index * 0.1,
                            ease: "easeOut",
                        }}
                    >
                        <defs>
                            <linearGradient
                                id={`gradient-${curve.id}`}
                                x1="0%"
                                y1="0%"
                                x2="100%"
                                y2="100%"
                                gradientUnits="userSpaceOnUse"
                            >
                                {curve.id === "curve9" || curve.id === "curve10" ? (
                                    <>
                                        <stop offset="0%" stopColor="#489BFF" />
                                        <stop offset="100%" stopColor="#B2EBFD" />
                                    </>
                                ) : (
                                    <>
                                        <stop offset="0%" stopColor="#B2EBFD" />
                                        <stop offset="100%" stopColor="#489BFF" />
                                    </>
                                )}
                            </linearGradient>
                        </defs>
                        <m.path
                            d={curve.path}
                            stroke={`url(#gradient-${curve.id})`}
                            strokeWidth={screenSize === "lg" || screenSize === "sm" ? 2 : 1.82961}
                            strokeLinejoin="round"
                            fill="none"
                            initial={{ pathLength: 0 }}
                            animate={isVisible ? { pathLength: 1 } : { pathLength: 0 }}
                            transition={{
                                duration: 2,
                                delay: index * 0.15,
                                ease: "easeInOut",
                            }}
                        />

                        {/* Styled Text Label positioned at curve endpoint */}
                        <foreignObject
                            x={curve.endPoint.x - 60}
                            y={curve.endPoint.y - 15}
                            width={screenSize === "mobile" ? 90 : 130}
                            height="60"
                        >
                            <m.div
                                className="inline-flex px-[14px] py-[6px] justify-center items-center gap-[10px] rounded-[12px] border-2 border-[#FAFCFF] bg-gradient-to-br from-[#E0EEFF] to-[#DDEEFF] font-Schibsted_Grotesk shadow-[0px_1px_4px_0px_rgba(13,83,189,0.25)] text-[#0F5295] text-center text-[8px] md:text-[12px] font-normal leading-[100%] whitespace-nowrap w-full"
                                style={{ fontFamily: "Schibsted Grotesk, sans-serif" }}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                                transition={{
                                    duration: 0.8,
                                    delay: index * 0.2 + 1,
                                    ease: "easeOut",
                                }}
                            >
                                {curve.text}
                            </m.div>
                        </foreignObject>
                    </m.svg>
                ))}
            </div>

            {/* Floating Elements */}
            <div className="absolute overflow-visible inset-0 pointer-events-none">
                {[...Array(8)].map((_, i) => (
                    <m.div
                        key={`float-${i}`}
                        className="absolute w-2 h-2 bg-blue-400/20 rounded-full"
                        style={{
                            left: `${10 + Math.random() * 80}%`,
                            top: `${10 + Math.random() * 80}%`,
                        }}
                        animate={isVisible ? {
                            y: [-20, 20, -20],
                            x: [-10, 10, -10],
                            opacity: [0.2, 0.6, 0.2],
                        } : {}}
                        transition={{
                            duration: 4 + Math.random() * 2,
                            repeat: Number.POSITIVE_INFINITY,
                            delay: Math.random() * 2,
                        }}
                    />
                ))}
            </div>
        </div>
    )
}